import React from "react";
import { ThemeProvider as StyledThemeProvider, DefaultTheme } from "styled-components";
import {
  Space,
  Typography,
  Tag,
  Alert,
  Spin,
  Descriptions,
} from "antd";
import {
  SendOutlined,
  ExclamationCircleOutlined,
  DollarOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import {
  StyledModal,
  NotesCard,
  ModalFooter,
  SubmitButton,
  UrgentAlert,
  HeaderSpace,
  TitleNoMargin,
  DescriptionsWithMargin,
  SectionTitle,
  NotesSection,
  NotesHeader,
  EditButton,
  NotesText,
  ClockIcon,
  DueByTextStyled,
  CloseButton,
  ModalContentWrapper,
  SectionCard,
  CustomerAvatar,
  CustomerInfo,
  StyledDivider,
  OrderDetailsContainer,
  OrderDetailRow,
  OrderDetailLabel,
  OrderDetailValue,
  OrderItemsList,
  OrderItemRow,
  OrderItemInfo,
  OrderItemPrice,
} from "./Styles";
import { DisputeDetailsModalProps } from "./types";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTheme } from "../../context/ThemeContext";

const { Text } = Typography;

const DisputeDetailsModal: React.FC<DisputeDetailsModalProps> = ({
  isOpen,
  onClose,
  selectedDispute,
  isLoadingDetails,
  responseNotes,
  isSubmitting,
  onNotesOpen,
  onSubmitResponse,
  getStatusBadge,
  isUrgent,
  canSubmitResponse,
}) => {
  const { isDarkMode } = useTheme();
  const styledTheme = { mode: isDarkMode ? "dark" : "light" } as DefaultTheme;

  if (!selectedDispute && !isLoadingDetails) return null;

  const customerName =
    selectedDispute?.evidence?.customerName ||
    selectedDispute?.order?.user_name ||
    "Unknown Customer";

  return (
    <StyledThemeProvider theme={styledTheme}>
      <StyledModal
      title={
        <Space>
          <SafetyOutlined style={{ color: "#1890ff" }} />
          <span>Dispute Details</span>
        </Space>
      }
      open={isOpen}
      onCancel={onClose}
      destroyOnClose
      zIndex={2000}
      width={800}
      footer={
        <ModalFooter>
          <CloseButton onClick={onClose}>Close</CloseButton>
          {selectedDispute && canSubmitResponse(selectedDispute) && (
            <SubmitButton
              type="primary"
              icon={<SendOutlined />}
              onClick={onSubmitResponse}
              loading={isSubmitting}
            >
              Submit Response to Stripe
            </SubmitButton>
          )}
        </ModalFooter>
      }
    >
      <Spin spinning={isLoadingDetails} tip="Loading dispute details...">
        {selectedDispute && (
          <ModalContentWrapper>
            {isUrgent(selectedDispute.status) && (
              <UrgentAlert
                message="Urgent: Response Required"
                description={
                  selectedDispute.dueBy && (
                    <Text>
                      Due by: {new Date(selectedDispute?.dueBy).toLocaleString()}
                    </Text>
                  )
                }
                type="error"
                icon={<ExclamationCircleOutlined />}
                showIcon
              />
            )}
            <HeaderSpace size="large">
              <CustomerAvatar size={64}>{customerName.charAt(0).toUpperCase()}</CustomerAvatar>
              <CustomerInfo>
                <TitleNoMargin level={4}>{customerName}</TitleNoMargin>
                <Text type="secondary">
                  Stripe Dispute:{" "}
                  {selectedDispute?.stripeDisputeId || selectedDispute?.id}
                </Text>
              </CustomerInfo>
            </HeaderSpace>
            <StyledDivider />
            <DescriptionsWithMargin column={2} bordered size="small">
              <Descriptions.Item label="Status">
                {getStatusBadge(selectedDispute?.status)}
              </Descriptions.Item>
              <Descriptions.Item label="Amount">
                <Space>
                  <DollarOutlined />
                  <Text strong>
                    {parseFloat(String(selectedDispute?.amount || 0)).toFixed(2)}{" "}
                    {selectedDispute?.currency?.toUpperCase() || "USD"}
                  </Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Reason">
                {selectedDispute?.reason
                  ? selectedDispute?.reason
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())
                  : "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Due By">
                <Space>
                  <ClockIcon isUrgent={isUrgent(selectedDispute?.status)} />
                  <DueByTextStyled isUrgent={isUrgent(selectedDispute?.status)}>
                    {new Date(selectedDispute?.dueBy).toLocaleString()}
                  </DueByTextStyled>
                </Space>
              </Descriptions.Item>
            </DescriptionsWithMargin>
            <StyledDivider />
            <SectionCard>
              <SectionTitle level={5}>Customer Information</SectionTitle>
              <Descriptions column={2} bordered size="small">
                <Descriptions.Item label="Name">
                  {selectedDispute?.evidence?.customerName}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  {selectedDispute?.evidence?.customerEmail}
                </Descriptions.Item>
                <Descriptions.Item label="Phone">
                  {selectedDispute?.evidence?.customerPhone}
                </Descriptions.Item>
              </Descriptions>
            </SectionCard>
            <SectionCard>
              <SectionTitle level={5}>Order Details</SectionTitle>
              <OrderDetailsContainer>
                <OrderDetailRow>
                  <OrderDetailLabel>Order ID</OrderDetailLabel>
                  <OrderDetailValue>
                    #{selectedDispute?.evidence?.orderDetails?.orderId || "N/A"}
                  </OrderDetailValue>
                </OrderDetailRow>
                <OrderDetailRow>
                  <OrderDetailLabel>Order Type</OrderDetailLabel>
                  <OrderDetailValue>
                    {selectedDispute?.evidence?.orderDetails?.orderType || "N/A"}
                  </OrderDetailValue>
                </OrderDetailRow>
                {selectedDispute?.evidence?.orderDetails?.items &&
                  selectedDispute?.evidence?.orderDetails?.items?.length > 0 && (
                    <div>
                      <OrderDetailLabel style={{ display: "block", marginBottom: "12px", fontWeight: 600 }}>
                        Items
                      </OrderDetailLabel>
                      <OrderItemsList>
                        {selectedDispute?.evidence?.orderDetails?.items?.map(
                          (item, idx) => (
                            <OrderItemRow key={idx}>
                              <OrderItemInfo>
                                {item?.quantity}x {item?.name}
                              </OrderItemInfo>
                              <OrderItemPrice>
                                ${parseFloat(String(item.price || 0)).toFixed(2)}
                              </OrderItemPrice>
                            </OrderItemRow>
                          )
                        )}
                      </OrderItemsList>
                    </div>
                  )}
                <OrderDetailRow>
                  <OrderDetailLabel>Subtotal</OrderDetailLabel>
                  <OrderDetailValue>
                    $
                    {parseFloat(
                      String(
                        (selectedDispute?.evidence?.orderDetails?.totalPrice ||
                          0) -
                          (selectedDispute?.evidence?.orderDetails?.tip || 0) -
                          (selectedDispute?.evidence?.orderDetails?.tax || 0) -
                          (selectedDispute?.evidence?.orderDetails
                            ?.deliveryFee || 0)
                      )
                    ).toFixed(2)}
                  </OrderDetailValue>
                </OrderDetailRow>
                {(selectedDispute?.evidence?.orderDetails?.tax || 0) > 0 && (
                  <OrderDetailRow>
                    <OrderDetailLabel>Tax</OrderDetailLabel>
                    <OrderDetailValue>
                      $
                      {parseFloat(
                        String(
                          selectedDispute?.evidence?.orderDetails?.tax || 0
                        )
                      ).toFixed(2)}
                    </OrderDetailValue>
                  </OrderDetailRow>
                )}
                {(selectedDispute?.evidence?.orderDetails?.deliveryFee || 0) > 0 && (
                  <OrderDetailRow>
                    <OrderDetailLabel>Delivery Fee</OrderDetailLabel>
                    <OrderDetailValue>
                      $
                      {parseFloat(
                        String(
                          selectedDispute?.evidence?.orderDetails?.deliveryFee ||
                            0
                        )
                      ).toFixed(2)}
                    </OrderDetailValue>
                  </OrderDetailRow>
                )}
                {(selectedDispute?.evidence?.orderDetails?.tip || 0) > 0 && (
                  <OrderDetailRow>
                    <OrderDetailLabel>Tip</OrderDetailLabel>
                    <OrderDetailValue>
                      $
                      {parseFloat(
                        String(
                          selectedDispute?.evidence?.orderDetails?.tip || 0
                        )
                      ).toFixed(2)}
                    </OrderDetailValue>
                  </OrderDetailRow>
                )}
                <OrderDetailRow isTotal>
                  <OrderDetailLabel isTotal>Total</OrderDetailLabel>
                  <OrderDetailValue isTotal>
                    $
                    {parseFloat(
                      String(
                        selectedDispute?.evidence?.orderDetails?.totalPrice || 0
                      )
                    ).toFixed(2)}
                  </OrderDetailValue>
                </OrderDetailRow>
                {selectedDispute?.evidence?.orderDetails?.deliveryAddress && (
                  <OrderDetailRow>
                    <OrderDetailLabel>Delivery Address</OrderDetailLabel>
                    <OrderDetailValue style={{ textAlign: "right", maxWidth: "60%" }}>
                      {selectedDispute?.evidence?.orderDetails?.deliveryAddress}
                    </OrderDetailValue>
                  </OrderDetailRow>
                )}
                {selectedDispute?.evidence?.orderDetails?.createdAt && (
                  <OrderDetailRow>
                    <OrderDetailLabel>Order Date</OrderDetailLabel>
                    <OrderDetailValue>
                      {new Date(
                        selectedDispute?.evidence?.orderDetails?.createdAt
                      ).toLocaleString()}
                    </OrderDetailValue>
                  </OrderDetailRow>
                )}
              </OrderDetailsContainer>
            </SectionCard>
            <SectionCard>
              <SectionTitle level={5}>Payment Proof</SectionTitle>
              <Descriptions column={2} bordered size="small">
                <Descriptions.Item label="Payment Intent ID">
                  {selectedDispute?.evidence?.paymentProof?.paymentIntentId}
                </Descriptions.Item>
                <Descriptions.Item label="Authorization Code">
                  {selectedDispute?.evidence?.paymentProof?.authorizationCode}
                </Descriptions.Item>
                <Descriptions.Item label="Amount">
                  <Text strong>
                    $
                    {parseFloat(
                      String(selectedDispute?.evidence?.paymentProof?.amount || 0)
                    ).toFixed(2)}{" "}
                    {selectedDispute?.evidence?.paymentProof?.currency?.toUpperCase() ||
                      "USD"}
                  </Text>
                </Descriptions.Item>
              </Descriptions>
            </SectionCard>
            <SectionCard>
              <SectionTitle level={5}>Delivery Confirmation</SectionTitle>
              <Descriptions column={2} bordered size="small">
                <Descriptions.Item label="Status">
                  <Tag color={selectedDispute?.evidence?.deliveryConfirmation?.status === "completed"  ? "green" : "orange"}>
                    {selectedDispute?.evidence?.deliveryConfirmation?.status || "N/A"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Completed At">
                  {new Date(
                    selectedDispute?.evidence?.deliveryConfirmation?.completedAt
                  ).toLocaleString()}
                </Descriptions.Item>
              </Descriptions>
            </SectionCard>
            <StyledDivider />
            <NotesSection>
              <NotesHeader>
                <TitleNoMargin level={5}>Response Notes</TitleNoMargin>
                <EditButton onClick={onNotesOpen}>
                  <FontAwesomeIcon icon={faPen} fontSize={12} /> Edit
                </EditButton>
              </NotesHeader>
              <NotesCard>
                <NotesText>
                  {selectedDispute.responseNotes ||
                    responseNotes ||
                    "No notes added yet. Click Edit to add notes."}
                </NotesText>
              </NotesCard>
            </NotesSection>
            {selectedDispute.submittedAt && (
              <Alert
                message="Response Submitted"
                description={
                  <Text>
                    Submitted at:{" "}
                    {new Date(selectedDispute.submittedAt).toLocaleString()}
                  </Text>
                }
                type="success"
                showIcon
                style={{
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(82, 196, 26, 0.15)",
                }}
              />
            )}
          </ModalContentWrapper>
        )}
      </Spin>
    </StyledModal>
    </StyledThemeProvider>
  );
};

export default DisputeDetailsModal;
